/**
 * OSMDExerciseScene - scene expander (MUS-317, Phase 1 "7b").
 *
 * Expands one playable take of the Exercise Section's `stepwise_run` recipe -
 * (recipe prompt x cell coordinate x frontier configuration) - into BOTH the
 * notation (a MusicXML string the scene feeds to OSMDPlayableScene) and the
 * judged-note stream, from ONE computation, per the MUS-317 consumption
 * contract (documentation/exercise-section/05-content-authoring.md section 1).
 *
 * What this owes (MUS-317):
 * - Interval-walk expansion from the recipe's `intervals` (carried as data, not
 *   hardcoded formulas): the traversal is recipe-driven, and key spelling is
 *   derived from the scale itself (not the figure), so a different figure over the
 *   same scale is a different interval list rather than new expansion code.
 * - Proximity-anchored register: the RH line starts on the tonic instance
 *   nearest the range-selected anchor, ties resolve DOWNWARD; the LH line always
 *   sits `lh_offset_octaves` below that RH base, even when the left hand plays
 *   alone.
 * - Honest key spelling: notes are spelled by the cell tonic's major key (F# in
 *   G major, never Gb), and the key signature is emitted.
 * - Chord-capable output path: notes carry a midi ARRAY (this figure is melodic,
 *   one pitch per event, but the pipeline never bakes in one-pitch-per-event).
 *
 * Deliberately deferred to the display-defaults seam (MUS-317 section 1.6): the
 * extreme-register octave-symbol policy (8va/8vb beyond ~3 octaves). Pitches are
 * emitted honestly; very high/low notes render with ledger lines for now, and
 * the octave-shift symbols land with the customizable display settings. No
 * mid-run clef changes are ever emitted.
 *
 * This module is PURE (no Phaser, no OSMD, no React) so the music-theory logic
 * is unit-tested in isolation. The scene binds it to the session machine.
 */

import type { StepwiseRunPrompt } from 'Types/ExerciseRecipeTypes';
import type {
  Direction,
  Hand,
  RhythmDensity,
  ScaleCell,
  ScaleConfig,
  Tonic,
} from 'Types/ExerciseContentTypes';
import {
  buildLiveComposerMusicXml,
  type AccidentalSpelling,
  type LiveComposerEvent,
  type TimeSignatureOption,
} from 'Containers/Repertoire/CreateMusicXml';

/** One expanded, judgeable note of the run (chord-capable via `midiNotes`). */
export interface JudgedNote {
  /** Position in the emitted judged-note stream (staff-flattened). */
  index: number;
  /** The pitch(es) sounding at this note. Melodic here => length 1. */
  midiNotes: number[];
  /** Which hand/staff plays it. */
  hand: 'RH' | 'LH';
  /** Absolute onset in MusicXML input divisions (DIVISIONS=4 per quarter). */
  startDivisions: number;
  /** Note length in the same division space (quarter=4, eighth=2, 16th=1). */
  durationDivisions: number;
}

/** The single-computation output: notation + the judged stream that mirrors it. */
export interface ExpandedRun {
  /** MusicXML for OSMDPlayableScene to ingest. */
  musicXml: string;
  /** Grand-staff measures the notation occupies. */
  measureCount: number;
  /** The judged-note stream, ordered by (onset, RH-before-LH). */
  judgedNotes: JudgedNote[];
}

const LETTER_ORDER = ['C', 'D', 'E', 'F', 'G', 'A', 'B'] as const;
const NATURAL_SEMITONE: Record<(typeof LETTER_ORDER)[number], number> = {
  C: 0,
  D: 2,
  E: 4,
  F: 5,
  G: 7,
  A: 9,
  B: 11,
};

/** Fifths for each tonic's MAJOR key (drives the emitted key signature). */
const MAJOR_KEY_FIFTHS: Record<Tonic, number> = {
  C: 0,
  G: 1,
  D: 2,
  A: 3,
  E: 4,
  B: 5,
  'F#': 6,
  Db: -5,
  Ab: -4,
  Eb: -3,
  Bb: -2,
  F: -1,
};

/** rhythm_density -> note length in DIVISIONS=4 input space. */
const RHYTHM_DIVISIONS: Record<RhythmDensity, number> = {
  quarter: 4,
  eighth: 2,
  sixteenth: 1,
};

const ALTER_TO_SPELLING: Record<number, AccidentalSpelling> = {
  [-2]: 'double-flat',
  [-1]: 'flat',
  [0]: 'natural',
  [1]: 'sharp',
  [2]: 'double-sharp',
};

const SEMITONES_PER_OCTAVE = 12;

/** The major scale's own step pattern - the basis for honest key spelling,
 *  independent of whatever figure (`prompt.intervals`) traverses the scale. */
const MAJOR_SCALE_INTERVALS = [2, 2, 1, 2, 2, 2, 1];

/**
 * The serializer's default ("auto") spelling per pitch class - naturals, and
 * sharps for the black keys (mirrors CreateMusicXml's pitchClassMap). We only
 * emit an explicit accidental spelling when the key's correct spelling DIFFERS
 * from this default; otherwise the default already spells the note right and adds
 * no visible accidental, which is what a key signature is for. Emitting a spelling
 * for a note the default already gets right forces a redundant accidental (e.g. a
 * natural sign on every note in C major).
 */
const AUTO_PITCH_CLASS: ReadonlyArray<{ step: string; alter: number }> = [
  { step: 'C', alter: 0 },
  { step: 'C', alter: 1 },
  { step: 'D', alter: 0 },
  { step: 'D', alter: 1 },
  { step: 'E', alter: 0 },
  { step: 'F', alter: 0 },
  { step: 'F', alter: 1 },
  { step: 'G', alter: 0 },
  { step: 'G', alter: 1 },
  { step: 'A', alter: 0 },
  { step: 'A', alter: 1 },
  { step: 'B', alter: 0 },
];

/** Parse a tonic label ('C', 'F#', 'Db', ...) into its letter, alter, pitch class. */
const parseTonic = (tonic: Tonic): { letter: (typeof LETTER_ORDER)[number]; pc: number } => {
  const letter = tonic[0] as (typeof LETTER_ORDER)[number];
  const accidentals = tonic.slice(1);
  const alter = (accidentals.match(/#/g)?.length ?? 0) - (accidentals.match(/b/g)?.length ?? 0);
  const pc = (NATURAL_SEMITONE[letter] + alter + 120) % SEMITONES_PER_OCTAVE;
  return { letter, pc };
};

/** Parse scientific pitch notation ('C4', 'C3', 'F#5') into a MIDI number. */
const parseSpn = (spn: string): number => {
  const match = spn.match(/^([A-G])(#{0,2}|b{0,2})(-?\d+)$/);
  if (!match) {
    throw new Error(`OSMDExerciseScene.expander: unparseable pitch anchor "${spn}"`);
  }
  const [, letter, accidentals, octaveStr] = match;
  const alter = (accidentals.match(/#/g)?.length ?? 0) - (accidentals.match(/b/g)?.length ?? 0);
  const octave = parseInt(octaveStr, 10);
  return (octave + 1) * SEMITONES_PER_OCTAVE + NATURAL_SEMITONE[letter as never] + alter;
};

/**
 * The MIDI instance of pitch class `pc` nearest `anchorMidi`; ties resolve
 * DOWNWARD (runs extend upward, so the lower choice centers better).
 */
const nearestPitchClassMidi = (pc: number, anchorMidi: number): number => {
  const anchorPc =
    ((anchorMidi % SEMITONES_PER_OCTAVE) + SEMITONES_PER_OCTAVE) % SEMITONES_PER_OCTAVE;
  const lower = anchorMidi - ((anchorPc - pc + SEMITONES_PER_OCTAVE) % SEMITONES_PER_OCTAVE);
  if (lower === anchorMidi) return anchorMidi;
  const upper = lower + SEMITONES_PER_OCTAVE;
  const distLower = anchorMidi - lower;
  const distUpper = upper - anchorMidi;
  return distLower <= distUpper ? lower : upper; // <= => ties resolve downward
};

/**
 * Honest spelling for each pitch class of the tonic's major scale, computed by
 * walking the SCALE's own step intervals with one consecutive letter name per
 * degree (heptatonic), so each degree gets the correct letter+accidental of the
 * key. Returns a pitch-class -> spelling map. `scaleIntervals` is the scale's
 * step pattern (e.g. the major scale), not the traversing figure's intervals -
 * the map covers every scale degree regardless of which figure plays them.
 */
const majorKeyPitchClassSpelling = (
  tonic: Tonic,
  scaleIntervals: number[],
): Record<number, AccidentalSpelling> => {
  const { letter, pc: tonicPc } = parseTonic(tonic);
  const tonicLetterIndex = LETTER_ORDER.indexOf(letter);
  const pcToSpelling: Record<number, AccidentalSpelling> = {};

  // Cumulative semitone offset of each in-octave degree (0-based).
  let running = 0;
  for (let degree = 0; degree < scaleIntervals.length; degree += 1) {
    const degreePc = (tonicPc + running) % SEMITONES_PER_OCTAVE;
    const degreeLetter = LETTER_ORDER[(tonicLetterIndex + degree) % LETTER_ORDER.length];
    let alter = degreePc - NATURAL_SEMITONE[degreeLetter];
    // Normalize the letter<->pitch gap into an accidental in [-6, 6].
    while (alter > 6) alter -= SEMITONES_PER_OCTAVE;
    while (alter < -6) alter += SEMITONES_PER_OCTAVE;
    running += scaleIntervals[degree];
    // Skip degrees the serializer's default already spells correctly - emitting a
    // spelling there would force a redundant visible accidental (the key signature
    // covers them). Only diverging notes (flat-key flats, E#/B# in F#/C#) get one.
    const auto = AUTO_PITCH_CLASS[degreePc];
    if (auto.step === degreeLetter && auto.alter === alter) continue;
    pcToSpelling[degreePc] = ALTER_TO_SPELLING[alter] ?? 'auto';
  }
  return pcToSpelling;
};

/**
 * The ascending semitone offsets of the walk (relative to the base tonic), for
 * `range` octaves of `stepIntervals`. Ends on the top tonic (offset 12*range).
 */
const ascendingOffsets = (stepIntervals: number[], rangeOctaves: number): number[] => {
  const offsets = [0];
  let running = 0;
  const steps = rangeOctaves * stepIntervals.length;
  for (let k = 0; k < steps; k += 1) {
    running += stepIntervals[k % stepIntervals.length];
    offsets.push(running);
  }
  return offsets;
};

/** Apply the configured direction to the ascending offsets. */
const directedOffsets = (ascending: number[], direction: Direction): number[] => {
  if (direction === 'asc') return ascending;
  if (direction === 'desc') return [...ascending].reverse();
  // 'both': asc then back down without doubling the top note, ending on the tonic.
  return [...ascending, ...ascending.slice(0, -1).reverse()];
};

const timeSignatureOption = ([beats, beatType]: [number, number]): TimeSignatureOption => ({
  id: `${beats}/${beatType}`,
  label: `${beats}/${beatType}`,
  beats,
  beatType,
});

const handsToRender = (hand: Hand): Array<'RH' | 'LH'> => (hand === 'HT' ? ['RH', 'LH'] : [hand]);

/**
 * Expand a `stepwise_run` take into notation + judged stream from one walk.
 */
export const expandStepwiseRun = (
  prompt: StepwiseRunPrompt,
  cell: ScaleCell,
  config: ScaleConfig,
): ExpandedRun => {
  const anchorSpn = prompt.register.rh_anchor_by_range[config.range_octaves];
  if (anchorSpn == null) {
    throw new Error(
      `OSMDExerciseScene.expander: no register anchor for range ${config.range_octaves}`,
    );
  }

  const { pc: tonicPc } = parseTonic(cell.tonic);
  const baseRhMidi = nearestPitchClassMidi(tonicPc, parseSpn(anchorSpn));
  const lhBaseMidi = baseRhMidi + prompt.register.lh_offset_octaves * SEMITONES_PER_OCTAVE;

  const offsets = directedOffsets(
    ascendingOffsets(prompt.intervals, config.range_octaves),
    config.direction,
  );
  const pcSpelling = majorKeyPitchClassSpelling(cell.tonic, MAJOR_SCALE_INTERVALS);
  const durationDivisions = RHYTHM_DIVISIONS[config.rhythm_density];
  const hands = handsToRender(config.hand);

  const events: LiveComposerEvent[] = [];
  const judgedNotes: JudgedNote[] = [];

  offsets.forEach((offset, step) => {
    const startDivisions = step * durationDivisions;
    hands.forEach((hand) => {
      const midi = (hand === 'RH' ? baseRhMidi : lhBaseMidi) + offset;
      const spelling =
        pcSpelling[((midi % SEMITONES_PER_OCTAVE) + SEMITONES_PER_OCTAVE) % SEMITONES_PER_OCTAVE];
      events.push({
        id: `r${step}-${hand}`,
        midiNotes: [midi],
        accidentalSpellings: spelling ? { [midi]: spelling } : undefined,
        capturedAtMs: 0,
        source: 'manual',
        restBeforeDivisions: 0,
        durationDivisions,
        startDivisions,
        staff: hand === 'RH' ? 1 : 2,
      });
      judgedNotes.push({
        index: judgedNotes.length,
        midiNotes: [midi],
        hand,
        startDivisions,
        durationDivisions,
      });
    });
  });

  // Order events by onset, RH (staff 1) before LH (staff 2) at a shared onset.
  events.sort(
    (a, b) => (a.startDivisions ?? 0) - (b.startDivisions ?? 0) || (a.staff ?? 1) - (b.staff ?? 1),
  );

  const { xml, measureCount } = buildLiveComposerMusicXml(
    events,
    [],
    timeSignatureOption(prompt.time_signature),
    // minimumMeasureCount 1: the shared builder pads to 4 measures by default (a
    // live-composer blank-score convention). A run must end with its last note, or
    // the score trails empty bars (rendered as a multirest) and the take runs on.
    { keySignatureFifths: MAJOR_KEY_FIFTHS[cell.tonic], minimumMeasureCount: 1 },
  );

  return { musicXml: xml, measureCount, judgedNotes };
};
